import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { portfolio as portfolioApi, accounts as accountsApi, algos as algosApi, kill } from "../api/endpoints";
import type { Account, Algo, SleeveAlert, TreasuryYields } from "../api/types";
import { SleevePerformanceMatrix } from "../components/SleevePerformanceMatrix";
import { AlgoSentimentBoard } from "../components/AlgoSentimentBoard";
import { SleeveCalendarPanel } from "../components/SleeveCalendarPanel";
import { KeyIndicatorsPanel } from "../components/KeyIndicatorsPanel";
import { ServerHealthPanel } from "../components/ServerHealthPanel";
import { PortfolioMetrics } from "../components/PortfolioMetrics";
import { AccountPicker } from "../components/AccountPicker";
import { PositionsPanel } from "../components/PositionsPanel";
import { AlertHistory } from "../components/AlertHistory";
// import { StatusBar } from "../components/StatusBar";
import { CommandPalette } from "../components/CommandPalette";
import { useWsEvent } from "../api/useWsEvent";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useActiveLinkedAlgoIds } from "../hooks/useActiveLinkedAlgoIds";
import type { LiveAlerts } from "../api/useWsEvent";

const HOT_REFETCH_MS = 2500;
const MED_REFETCH_MS = 5000;

export function Desk() {
  const qc = useQueryClient();

  const accountsQ = useQuery({
    queryKey: ["accounts"],
    queryFn: accountsApi.list,
    refetchInterval: 10_000,
  });

  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get("scope") ?? localStorage.getItem("nexus-desk-scope");
    return raw && raw !== "ALL" && /^\d+$/.test(raw) ? Number(raw) : null;
  });
  const list = accountsQ.data ?? [];
  useEffect(() => {
    if (selectedId !== null && !list.some((a) => a.id === selectedId && !a.retired)) {
      setSelectedId(null);
    }
  }, [list, selectedId]);
  // Balance math: use /accounts/{id}/pnl (raw journal SUM(pnl_net) for the
  // account, retired sleeves included) for sim accounts — the same formula
  // the selected-account view uses (Desk.tsx:272). The old source here was
  // portfolio/metrics total_pnl, which used to exclude retired-sleeve
  // history and made the ALL ticker disagree with the selected view
  // (e.g. $4,470 vs $5,025 — the $555 delta was a retired sleeve's sim trade).
  const pnlQueries = useQueries({
    queries: list
      .filter((a) => !a.retired && a.kind === "sim")
      .map((a) => ({
        queryKey: ["accounts", a.id, "pnl"],
        queryFn: () => accountsApi.totalPnl(a.id).then((r) => ({ id: a.id, pnl: r.pnl })),
        staleTime: 30_000,
      })),
  });
  const pnlMap = new Map(pnlQueries.filter((q) => q.data).map((q) => [q.data!.id, q.data!.pnl]));
  const account: Account | undefined = useMemo(() => {
    if (selectedId !== null) return list.find((a) => a.id === selectedId && !a.retired);
    return list.find((a) => !a.retired);
  }, [list, selectedId]);

  const scopeAccountId = selectedId;
  const isAggregateScope = scopeAccountId == null;
  useEffect(() => {
    const raw = scopeAccountId == null ? "ALL" : String(scopeAccountId);
    localStorage.setItem("nexus-desk-scope", raw);
    const params = new URLSearchParams(location.search);
    params.set("scope", raw);
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", newUrl);
  }, [scopeAccountId]);

  const onAccountSelect = (id: number | null) => {
    setSelectedId(id);
  };

  const algosQ = useQuery<Algo[]>({
    // ALL scope → global roster (union of sleeves linked to any account, eval-
    // stamped by the engine). Single-account scope → that account's links.
    // The old fallback (`list.find(a => !a.retired)`) fed the sentiment board,
    // calendar, and positions panel with the FIRST account's sleeves (AMP cash)
    // even in ALL view — that was the "only cash-account links" bug.
    queryKey: isAggregateScope ? ["algos", "all"] : ["account", scopeAccountId, "algos"],
    queryFn: isAggregateScope
      ? () => algosApi.list()
      : () => accountsApi.algos(scopeAccountId!),
    enabled: isAggregateScope || scopeAccountId != null,
    refetchInterval: HOT_REFETCH_MS,
  });

  const portfolioMetricsQ = useQuery({
    queryKey: ["portfolio", "metrics", scopeAccountId],
    queryFn: () => portfolioApi.metrics(scopeAccountId ?? undefined),
    refetchInterval: MED_REFETCH_MS,
  });

  const sleevePerfQ = useQuery({
    queryKey: ["portfolio", "sleeves", scopeAccountId],
    queryFn: () => portfolioApi.sleeves(scopeAccountId ?? undefined),
    refetchInterval: MED_REFETCH_MS,
  });

  // Active-link set shared by the sleeve matrix, sentiment board, calendar,
  // and positions panel. ALL view → union of active links across every
  // non-retired account (the accounts viewable in the picker); single-account
  // view → that account's active links only. null → links not loaded / fetch
  // failed → fail open (unfiltered).
  const { linkedAlgoIds } = useActiveLinkedAlgoIds(list, scopeAccountId);
  const visibleSleeves = useMemo(() => {
    const all = sleevePerfQ.data ?? [];
    return linkedAlgoIds == null ? all : all.filter((s) => linkedAlgoIds.has(s.algo_id));
  }, [sleevePerfQ.data, linkedAlgoIds]);
  // Same filter for the sentiment board / calendar / positions panel: only
  // actively-linked sleeves are real (paused links / retired accounts never
  // receive orders). ALL scope filters the global roster to the cross-account
  // union; single-account scope drops that account's paused links.
  const visibleAlgos = useMemo(() => {
    const all = algosQ.data ?? [];
    return linkedAlgoIds == null ? all : all.filter((a) => linkedAlgoIds.has(a.id));
  }, [algosQ.data, linkedAlgoIds]);

  // Compute portfolio-level profit factor from per-sleeve data
  // PF = total_gross_profit / total_gross_loss
  // Derive gross_profit/gross_loss from pnl_total and profit_factor:
  //   PF = gp / gl  →  gp = PF * gl
  //   pnl = gp - gl = gl * (PF - 1)
  //   gl = pnl / (PF - 1)  [gl is absolute value, positive]
  //   gp = PF * gl
  const portfolioPf = (() => {
    const sleeves = visibleSleeves;
    let totalGp = 0;
    let totalGl = 0;
    for (const s of sleeves) {
      const pf = s.profit_factor;
      const pnl = s.pnl_total;
      if (pf == null || pnl === 0 || pf === 1) continue;
      if (pf === 0) {
        // No winning trades — all pnl is loss
        if (pnl < 0) totalGl += Math.abs(pnl);
        continue;
      }
      if (!isFinite(pf)) {
        // Infinite PF = no losses
        if (pnl > 0) totalGp += pnl;
        continue;
      }
      const glAbs = Math.abs(pnl / (pf - 1));
      const gp = pf * glAbs;
      totalGp += gp;
      totalGl += glAbs;
    }
    return totalGl > 0 ? totalGp / totalGl : totalGp > 0 ? Infinity : null;
  })();

  const marketWidgetsQ = useQuery({
    queryKey: ["portfolio", "marketWidgets"],
    queryFn: portfolioApi.marketWidgets,
    refetchInterval: 30_000,
  });

  const alertsQ = useQuery({
    queryKey: ["portfolio", "alerts", scopeAccountId],
    queryFn: () => portfolioApi.alerts(scopeAccountId ?? undefined),
    refetchInterval: MED_REFETCH_MS,
  });

  const killQ = useQuery({
    queryKey: ["kill"],
    queryFn: kill.status,
    refetchInterval: 3000,
  });

  // ── Live WS state ─────────────────────────────────────────────────
  const [liveAlerts, setLiveAlerts] = useState<LiveAlerts | null>(null);
  // wsConnected display removed (commented out) but setter still wired to WS
  // state — keep the slot for when the LIVE/POLL indicator returns.
  const [, setWsConnected] = useState(false);
  const [alertHistory, setAlertHistory] = useState<SleeveAlert[]>([]);
  const maxHistory = useRef(200);
  const [liveMarketData, setLiveMarketData] = useState<{ vix: number; vxn: number; dxy: number; yields: TreasuryYields; vix_gate_blocked: boolean } | null>(null);

  const onHealthAlert = useCallback((alerts: LiveAlerts) => {
    setLiveAlerts(alerts);
    const combined: SleeveAlert[] = [
      ...alerts.critical,
      ...alerts.warnings,
    ].map((a) => ({ ...a, timestamp: a.timestamp ?? new Date().toISOString() }));
    setAlertHistory((prev) => {
      const next = [...combined, ...prev];
      if (next.length > maxHistory.current) next.length = maxHistory.current;
      return next;
    });
  }, []);

  const onConnectionChange = useCallback((state: string) => {
    setWsConnected(state === "connected");
  }, []);

  const onMarketDataUpdate = useCallback((data: { vix: number; vxn: number; dxy: number; yields: TreasuryYields; vix_gate_blocked: boolean }) => {
    setLiveMarketData(data);
  }, []);

  useWsEvent({ onHealthAlert, onConnectionChange, onMarketDataUpdate });

  const activeAlerts = alertsQ.data ?? [];
  const alerts = liveAlerts ?? {
    critical: activeAlerts.filter((a) => a.severity === "critical"),
    warnings: activeAlerts.filter((a) => a.severity !== "critical"),
    timestamp: Date.now(),
  };

  const killEngaged = !!killQ.data?.engaged;

  // ── Keyboard shortcuts ────────────────────────────────────────────
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const doRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["portfolio"] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
    qc.invalidateQueries({ queryKey: ["kill"] });
  }, [qc]);

  const doKill = useCallback(() => {
    if (isAggregateScope) return;
    if (killEngaged) {
      kill.clear(account?.id).then(() => qc.invalidateQueries({ queryKey: ["kill"] }));
    } else {
      if (window.confirm("ENGAGE KILL?")) {
        kill.engage("account", account?.id, "operator").then(() => qc.invalidateQueries({ queryKey: ["kill"] }));
      }
    }
  }, [killEngaged, account?.id, qc, isAggregateScope]);

  const doFlatten = useCallback(() => {
    if (!isAggregateScope && account && window.confirm("FLATTEN ALL positions?")) {
      accountsApi.flatten(account.id).then(() => {
        qc.invalidateQueries({ queryKey: ["portfolio"] });
          });
    }
  }, [account, qc, isAggregateScope]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  useKeyboardShortcuts({
    shortcuts: {
      "k+control": { label: "Kill", description: "Toggle kill switch", action: doKill },
      "r+control": { label: "Refresh", description: "Force refresh all data", action: doRefresh },
      "/+control": { label: "Search", description: "Open command palette", action: openPalette },
      "?+shift": { label: "Shortcuts", description: "Toggle shortcut cheat-sheet", action: () => setShowShortcuts((v) => !v) },
    },
  });

  if (accountsQ.isLoading) {
    return <div className="muted">loading accounts…</div>;
  }

  const runningCount = visibleAlgos.filter((a) => a.status === "running").length ?? 0;

  const balance = account
    ? account.kind === "sim"
      ? (account.starting_balance ?? 0) + (pnlMap.get(account.id) ?? 0)
      : (account.rithmic?.last_balance ?? account.starting_balance ?? 0)
    : 0;

  const md = liveMarketData
    ? { vix: liveMarketData.vix, vxn: liveMarketData.vxn, dxy: liveMarketData.dxy, yields: liveMarketData.yields }
    : marketWidgetsQ.data
      ? { vix: marketWidgetsQ.data.vix, vxn: marketWidgetsQ.data.vxn, dxy: marketWidgetsQ.data.dxy, yields: marketWidgetsQ.data.treasury_yields }
      : null;

  return (
    <div className="desk">
      <div className="desk-content">
      {showShortcuts && (
        <div className="shortcuts-hint" onClick={() => setShowShortcuts(false)}>
          <h3>Shortcuts</h3>
          {[
            { key: "Ctrl+K", desc: "Kill switch" },
            { key: "Ctrl+R", desc: "Refresh data" },
            { key: "Ctrl+/", desc: "Command palette" },
            { key: "Shift+?", desc: "This list" },
          ].map((s) => (
            <div key={s.key} className="shortcut-row">
              <kbd>{s.key}</kbd>
              <span className="muted">{s.desc}</span>
            </div>
          ))}
        </div>
      )}

      {paletteOpen && (
        <CommandPalette
          groups={[
            {
              title: "Controls",
              items: [
                { label: "Kill All Dispatch", shortcut: "Ctrl+K", action: doKill, danger: !killEngaged },
                { label: "Flatten All Positions", action: doFlatten, danger: true },
                { label: "Pause All Sleeves", action: () => {}, danger: true },
              ],
            },
            {
              title: "Navigation",
              items: [
                { label: "Go to Desk", shortcut: "Ctrl+1", action: () => {} },
                { label: "Go to Analytics", shortcut: "Ctrl+2", action: () => {} },
                { label: "Go to Sleeves", shortcut: "Ctrl+3", action: () => {} },
                { label: "Go to Accounts", shortcut: "Ctrl+4", action: () => {} },
              ],
            },
            {
              title: "Actions",
              items: [
                { label: "Refresh Portfolio", shortcut: "Ctrl+R", action: doRefresh },
                { label: "Export Performance Report", action: () => {} },
                { label: "Download Trade History", action: () => {} },
              ],
            },
          ]}
          onClose={closePalette}
        />
      )}

      {/* ── MARKET DATA STRIP ── */}
      <div className="desk-strip">
        <div className="desk-strip-left">
          {account && (
            <>
              <AccountPicker accounts={list} selectedId={selectedId} onSelect={onAccountSelect} showAll />
              {/* {isAggregateScope && <span className="pill dim">ALL ACCOUNTS</span>} */}
              <span className="desk-ticker-sep" />
              <span className="desk-ticker">
                <span className="desk-ticker-label">BAL</span>
                {scopeAccountId == null ? (
                  <span className="desk-ticker-value" style={{ fontSize: 10, display: "flex", gap: 10, flexWrap: "nowrap", overflowX: "auto" }}>
                    {list.filter((a) => !a.retired).map((a) => {
                      // Sim accounts: starting_balance + journal P&L (retired
                      // sleeves included) — identical formula to the
                      // selected-account view above. Live accounts: broker
                      // last_balance already reflects realized history.
                      const baseBal = a.kind === "sim"
                        ? (a.starting_balance ?? 0)
                        : (a.rithmic?.last_balance ?? a.starting_balance ?? 0);
                      const acctPnl = a.kind === "sim" ? (pnlMap.get(a.id) ?? 0) : 0;
                      const bal = baseBal + acctPnl;
                      return (
                        <span key={a.id} title={a.name} style={{ whiteSpace: "nowrap" }}>
                          <span className="pill dim" style={{ fontSize: 9, padding: "0 4px" }}>#{a.id}</span>
                          <span className={bal >= 0 ? "desk-ticker-ok" : "desk-ticker-bad"}>
                            ${bal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        </span>
                      );
                    })}
                  </span>
                ) : (
                  <span className={"desk-ticker-value " + (balance >= 0 ? "desk-ticker-ok" : "desk-ticker-bad")}>
                    ${balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </span>
              <span className="desk-ticker-sep" />
              {!isAggregateScope && <button className="btn btn-xs" onClick={doFlatten} title="Flatten all positions" aria-label="Flatten all positions">
                FLATTEN
              </button>}
            </>
          )}
        </div>
        <div className="desk-strip-center">
          {md && (
            <>
              <span className="desk-ticker">
                <span className="desk-ticker-label">VIX</span>
                <span className="desk-ticker-value">{md.vix?.toFixed(2) ?? "—"}</span>
              </span>
              <span className="desk-ticker">
                <span className="desk-ticker-label">VXN</span>
                <span className="desk-ticker-value">{md.vxn?.toFixed(2) ?? "—"}</span>
              </span>
              <span className="desk-ticker">
                <span className="desk-ticker-label">DXY</span>
                <span className="desk-ticker-value">{md.dxy?.toFixed(3) ?? "—"}</span>
              </span>
              <span className="desk-ticker">
                <span className="desk-ticker-label">2Y</span>
                <span className="desk-ticker-value">{md.yields.y2y > 0 ? md.yields.y2y.toFixed(2) : "—"}</span>
              </span>
              <span className="desk-ticker">
                <span className="desk-ticker-label">5Y</span>
                <span className="desk-ticker-value">{md.yields?.y5y?.toFixed(2) ?? "—"}</span>
              </span>
              <span className="desk-ticker">
                <span className="desk-ticker-label">10Y</span>
                <span className="desk-ticker-value">{md.yields?.y10y?.toFixed(2) ?? "—"}</span>
              </span>
              <span className="desk-ticker">
                <span className="desk-ticker-label">30Y</span>
                <span className="desk-ticker-value">{md.yields?.y30y?.toFixed(2) ?? "—"}</span>
              </span>
              <span className="desk-ticker-sep" />
            </>
          )}
          {runningCount > 0 && (
            <span className="desk-ticker">
              <span className="desk-ticker-label">RUNNING</span>
              <span className="desk-ticker-value desk-ticker-ok">{runningCount}</span>
            </span>
          )}
          {alerts.critical.length > 0 && (
            <span className="desk-ticker">
              <span className="desk-ticker-label">CRIT</span>
              <span className="desk-ticker-value desk-ticker-bad">{alerts.critical.length}</span>
            </span>
          )}
        </div>
        <div className="desk-strip-right">
          <PortfolioMetrics data={portfolioMetricsQ.data} portfolioPf={portfolioPf} />
          <span className="desk-ticker-sep" />
          {/* <span className={`desk-connect ${wsConnected ? "desk-connect-live" : "desk-connect-poll"}`}>
            {wsConnected ? "LIVE" : "POLL"}
          </span> */}
          {killEngaged && <span className="desk-kill-badge">KILL</span>}
        </div>
      </div>

      {/* Error banner — friendly messages instead of raw Rithmic errors */}
      {account?.rithmic?.last_error && (
        <div className="error-banner" style={{ marginTop: 0, marginBottom: 4 }}>
          {account.rithmic.last_error.includes("ORDER_PLANT login failed") || account.rithmic.last_error.includes("timed out") ? (
            <span>Markets closed — Rithmic disconnected (will reconnect on next RTH session open)</span>
          ) : account.rithmic.last_error.includes("boot resume failed") ? (
            <span>Rithmic connection unavailable — markets may be closed</span>
          ) : (
            <span>Rithmic: {account.rithmic.last_error}</span>
          )}
        </div>
      )}

      {/* ── MAIN CONTENT (3-col layout, fills viewport) ── */}
      <div className="desk-body">
        {/* Left slim col: positions, alerts, sentiment, server health.
            Alerts sit above sentiment: actionable > informational. */}
        <div className="desk-col-left">
          <PositionsPanel />
          <AlertHistory
            activeAlerts={alertsQ.data ?? []}
            history={alertHistory}
            onClearHistory={() => setAlertHistory([])}
            accounts={list}
            scopeAccountId={scopeAccountId}
          />
          <AlgoSentimentBoard algos={visibleAlgos} />
          <ServerHealthPanel />
        </div>
        {/* Center main col: sleeve matrix */}
        <div className="desk-col-center">
          <SleevePerformanceMatrix
            sleeves={visibleSleeves}
            algos={algosQ.data ?? []}
          />
        </div>
        {/* Right slim col: key indicators + sleeve calendar */}
        <div className="desk-col-right">
          <KeyIndicatorsPanel />
          <SleeveCalendarPanel algos={visibleAlgos} />
        </div>
      </div>
      </div>{/* end desk-content */}

    </div>
  );
}
